/**
 * Which strokes a sweep of the eraser touches. Tested against the ink itself,
 * never the node box: a curve's bounding box is mostly empty, and erasing a
 * stroke the pointer never went near is the worst thing a destructive tool can
 * do.
 */

import type { Node } from "@xyflow/react";
import { pointToSegmentDistance, readDrawingPoints } from "./drawingStroke";
import { drawingTipInk, normalizeDrawingTip } from "./drawingTips";
import {
  getNodeAbsoluteBounds,
  type GetInternalNode,
} from "./sectionMembership";

/** How close the sweep has to pass, in screen pixels. */
export const ERASER_RADIUS_PX = 12;

/** Closest approach between two segments — the sweep against one ink segment. */
function segmentDistance(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  dx: number,
  dy: number,
): number {
  const rx = bx - ax;
  const ry = by - ay;
  const sx = dx - cx;
  const sy = dy - cy;
  const denominator = rx * sy - ry * sx;
  if (Math.abs(denominator) > 1e-9) {
    const t = ((cx - ax) * sy - (cy - ay) * sx) / denominator;
    const u = ((cx - ax) * ry - (cy - ay) * rx) / denominator;
    if (t >= 0 && t <= 1 && u >= 0 && u <= 1) return 0;
  }
  return Math.min(
    pointToSegmentDistance(ax, ay, cx, cy, dx, dy),
    pointToSegmentDistance(bx, by, cx, cy, dx, dy),
    pointToSegmentDistance(cx, cy, ax, ay, bx, by),
    pointToSegmentDistance(dx, dy, ax, ay, bx, by),
  );
}

/** Half the width the stroke actually paints, so a wide wash is easier to hit. */
function inkReach(node: Node, radius: number): number {
  const data = node.data as { tip?: unknown; stroke_width?: unknown };
  const strokeWidth = Number(data.stroke_width) || 2;
  const ink = drawingTipInk(normalizeDrawingTip(data.tip), strokeWidth);
  return radius + ink.strokeWidth / 2;
}

/** Ids of the drawings whose ink the sweep a→b passes within `radius` of. */
export function drawingsUnderSweep(
  nodes: Node[],
  a: { x: number; y: number },
  b: { x: number; y: number },
  getInternalNode?: GetInternalNode,
  radius: number = ERASER_RADIUS_PX,
): string[] {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const hits: string[] = [];

  for (const node of nodes) {
    if (node.type !== "canvasDrawing" || node.hidden) continue;
    const points = readDrawingPoints(
      (node.data as { points?: unknown }).points,
    );
    if (points.length < 2) continue;

    const bounds = getNodeAbsoluteBounds(node, getInternalNode, nodesById);
    const reach = inkReach(node, radius);
    if (
      Math.min(a.x, b.x) - reach > bounds.x + bounds.width ||
      Math.max(a.x, b.x) + reach < bounds.x ||
      Math.min(a.y, b.y) - reach > bounds.y + bounds.height ||
      Math.max(a.y, b.y) + reach < bounds.y
    ) {
      continue;
    }

    // A tap leaves a single sample: nothing to sweep against but the dot.
    if (points.length === 2) {
      const distance = pointToSegmentDistance(
        bounds.x + points[0],
        bounds.y + points[1],
        a.x,
        a.y,
        b.x,
        b.y,
      );
      if (distance <= reach) hits.push(node.id);
      continue;
    }

    for (let i = 0; i + 3 < points.length; i += 2) {
      const distance = segmentDistance(
        a.x,
        a.y,
        b.x,
        b.y,
        bounds.x + points[i],
        bounds.y + points[i + 1],
        bounds.x + points[i + 2],
        bounds.y + points[i + 3],
      );
      if (distance <= reach) {
        hits.push(node.id);
        break;
      }
    }
  }

  return hits;
}
